// The one settle body for "a server excerpt snapshot lands on the detail
// cache" — shared by the coding mutations' onSuccess handlers AND the
// cable broadcast handler. A leaf module (keys + id normalization only)
// so both mutations.js and the cable handler import it directly: the
// previous arrangement reached it through a comment-guarded module cycle
// whose safety was an accident of import order.
import { excerptKeys } from './queries';
import { normalizeId } from '../utils/idNormalization';

// Lets the merge see in-flight coding removals and keep a snapshot
// generated before one committed from resurrecting its codings.
export const REMOVE_CODING_MUTATION_KEY = ['remove-coding'];

// Same idea for memos: an APPLYING snapshot generated before a local memo
// delete committed still carries that memo, and must not resurrect it.
export const REMOVE_MEMO_MUTATION_KEY = ['remove-memo'];

const memoTimestamp = (memo) => memo.updated_at || memo.created_at || null;

// Reconcile memo lists for an APPLYING snapshot (one the gate accepted).
// Memos are their own rows with their own timestamps, and both sides can
// be ahead: the cache holds local optimistic/just-settled memos a
// response generated earlier doesn't know about, and a broadcast carries
// a collaborator's memo the cache doesn't. Rules:
// - server memos land unless a local delete of that memo is in flight
//   (REMOVE_MEMO_MUTATION_KEY); shared ids take whichever copy has the
//   newer own timestamp (text edits — updated_at comes from
//   _memo.json.jbuilder; a tie keeps the local copy).
// - cache-only memos are kept while optimistic (isPending temp entries)
//   and kept when their own timestamp says the snapshot PREDATES them
//   (the snapshot simply doesn't know about them yet). A cache-only memo
//   the snapshot postdates was deleted server-side and is dropped — this
//   sweep is what heals a zombie memo whose MemosChannel destroy event
//   was lost to a websocket drop.
// Round 8 killed the previous add-only union: it could never remove, so
// memo deletion hinged on a single droppable websocket message, and it
// ran even for gate-SKIPPED snapshots, resurrecting locally-deleted memos
// from stale responses with no path that ever removed them again.
const reconcileMemos = (currentMemos, serverMemos, snapshotTimestamp, pendingMemoDeletionIds) => {
  const server = (Array.isArray(serverMemos) ? serverMemos : [])
    .filter((memo) => !pendingMemoDeletionIds.has(normalizeId(memo.id)));
  const current = currentMemos || [];
  if (!current.length) return server;

  const serverById = new Map(server.map((memo) => [memo.id, memo]));
  const merged = [];
  current.forEach((memo) => {
    if (serverById.has(memo.id)) {
      const serverCopy = serverById.get(memo.id);
      const serverTs = memoTimestamp(serverCopy);
      const localTs = memoTimestamp(memo);
      merged.push(serverTs && localTs && serverTs > localTs ? serverCopy : memo);
      serverById.delete(memo.id);
      return;
    }
    const localTs = memoTimestamp(memo);
    if (memo.isPending || !localTs || !snapshotTimestamp || localTs >= snapshotTimestamp) {
      merged.push(memo);
    }
    // else: authoritative drop — the snapshot postdates this memo and
    // does not carry it, so it was deleted server-side.
  });
  serverById.forEach((memo) => merged.push(memo));
  return merged;
};

// One body for "settle a server excerpt over the detail cache" — the
// trickiest merge rule in the codebase, shared by the add/remove/create
// mutations' onSuccess and the cable handler so it can never diverge.
// - STALE SNAPSHOTS ARE SKIPPED OUTRIGHT AND TOUCH NOTHING: server
//   updated_at is monotonic per row for coding changes (every coding
//   mutation stamps it), so a reordered older response or a delayed
//   broadcast must not overwrite a newer settle's write.
//   Settle gate is `<=` (a TIE means this client's own reordered
//   responses — the cache already reflects the newest applied settle).
//   Broadcast gate is `<` (a TIE must APPLY: two clients committing in
//   the same serialized millisecond is exactly what broadcasts exist to
//   deliver, and a memo-only broadcast always ties — the memo touch is an
//   async job, so its snapshot carries the PRE-touch excerpt updated_at).
//   Residual: cross-dyno clock skew beyond ties can still silence a
//   broadcast — documented on the PR; a monotonic version column is the
//   root fix.
// - keepLocal: only in-flight optimistic ADDS (optimistic_key) survive
//   the union; the server wins otherwise.
// - In-flight coding removals are excluded from the server list BY THE
//   PAIRS THEY ACTUALLY REMOVED (the onMutate-captured codings): the old
//   broad variables match (code_id + empty userIds = every user) also
//   stripped a COLLABORATOR's coding of the same code committed after
//   the local removal — new server truth this client would then never
//   render. Variables remain the fallback for a missing detail entry,
//   where onMutate captured no pairs.
// - Memos reconcile per-memo (see reconcileMemos above).
export const mergeServerExcerptIntoDetail = (queryClient, serverExcerpt, { broadcast = false } = {}) => {
  const serverExcerptId = normalizeId(serverExcerpt.id);

  const pendingRemovals = queryClient.getMutationCache()
    .findAll({ mutationKey: REMOVE_CODING_MUTATION_KEY, status: 'pending' })
    .filter((mutation) => {
      const variables = mutation.state.variables;
      return variables && normalizeId(variables.excerptId) === serverExcerptId;
    });
  const removedPairSet = new Set();
  const broadFallback = [];
  pendingRemovals.forEach((mutation) => {
    const captured = mutation.state.context?.removedCodings;
    if (captured?.length) {
      captured.forEach((coding) =>
        removedPairSet.add(`${normalizeId(coding.code_id)}:${normalizeId(coding.user_id)}`));
    } else {
      broadFallback.push(mutation.state.variables);
    }
  });
  const removedByPending = (coding) =>
    removedPairSet.has(`${normalizeId(coding.code_id)}:${normalizeId(coding.user_id)}`) ||
    broadFallback.some((variables) =>
      normalizeId(variables.codeId) === normalizeId(coding.code_id) &&
      (!(variables.userIds || []).length ||
        variables.userIds.map(normalizeId).includes(normalizeId(coding.user_id))));

  const serverCodings = (serverExcerpt.codings || []).filter((coding) => !removedByPending(coding));
  const inServer = (c) =>
    serverCodings.some((s) => s.code_id === c.code_id && s.user_id === c.user_id);
  const keepLocal = (c) => c.optimistic_key && !inServer(c);

  const pendingMemoDeletionIds = new Set(
    queryClient.getMutationCache()
      .findAll({ mutationKey: REMOVE_MEMO_MUTATION_KEY, status: 'pending' })
      .map((mutation) => mutation.state.variables)
      .filter((variables) => variables && normalizeId(variables.excerptId) === serverExcerptId)
      .map((variables) => normalizeId(variables.memoId))
  );

  queryClient.setQueryData(excerptKeys.detail(serverExcerptId), (current) => {
    if (current?.updated_at && serverExcerpt.updated_at &&
        (broadcast
          ? serverExcerpt.updated_at < current.updated_at
          : serverExcerpt.updated_at <= current.updated_at)) {
      return current;
    }
    const localKept = ((current || {}).codings || []).filter(keepLocal);
    return {
      ...(current || {}),
      ...serverExcerpt,
      codings: [...serverCodings, ...localKept],
      memos: reconcileMemos(
        current?.memos,
        serverExcerpt.memos,
        serverExcerpt.updated_at,
        pendingMemoDeletionIds
      ),
    };
  });
};
